import React, { useState } from 'react';
import { STAGE_LABELS, STAGE_COLORS, STANDARD_STAGES, EMERGENCY_STAGES, LEAD_SOURCES } from './constants';
import { Search } from 'lucide-react';

export default function LeadsTable({ leads, users, filters, setFilters, onOpen }) {
  const [q, setQ] = useState(filters.search || '');

  const applySearch = () => setFilters(f => ({ ...f, search: q }));

  const inputCls = 'bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white text-xs focus:border-orange-500/50 focus:outline-none';

  return (
    <div data-testid="leads-table">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-1">
          <input data-testid="leads-search-input" className={inputCls} placeholder="Search name, phone, email, address..." value={q}
            onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && applySearch()} style={{ minWidth: 240 }} />
          <button onClick={applySearch} data-testid="leads-search-btn" className="bg-orange-500 hover:bg-orange-600 text-white rounded p-2"><Search className="w-4 h-4" /></button>
        </div>
        <select className={inputCls} value={filters.lead_type || ''} onChange={e => setFilters(f => ({ ...f, lead_type: e.target.value }))} data-testid="filter-type">
          <option value="">All Types</option>
          <option value="standard">Standard</option>
          <option value="emergency">Emergency</option>
        </select>
        <select className={inputCls} value={filters.status || ''} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} data-testid="filter-status">
          <option value="">All Statuses</option>
          {[...new Set([...STANDARD_STAGES, ...EMERGENCY_STAGES])].map(s => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
        </select>
        <select className={inputCls} value={filters.source || ''} onChange={e => setFilters(f => ({ ...f, source: e.target.value }))} data-testid="filter-source">
          <option value="">All Sources</option>
          {LEAD_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className={inputCls} value={filters.language || ''} onChange={e => setFilters(f => ({ ...f, language: e.target.value }))} data-testid="filter-language">
          <option value="">All Languages</option>
          <option value="en">English</option>
          <option value="es">Spanish</option>
        </select>
        <select className={inputCls} value={filters.assigned_to || ''} onChange={e => setFilters(f => ({ ...f, assigned_to: e.target.value }))} data-testid="filter-assigned">
          <option value="">All Assignees</option>
          {users.map(u => <option key={u.id} value={u.email}>{u.name}</option>)}
        </select>
        <input type="date" className={inputCls} value={filters.date_from || ''} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} data-testid="filter-date-from" />
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-slate-400 text-xs">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Service</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Source</th>
                <th className="text-left px-4 py-3">Lang</th>
                <th className="text-left px-4 py-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 && <tr><td colSpan={7} className="text-center text-slate-500 py-10">No leads match your filters</td></tr>}
              {leads.map(l => (
                <tr key={l.id} onClick={() => onOpen(l.id)} data-testid={`lead-row-${l.id}`}
                  className="border-t border-slate-700 hover:bg-slate-700/40 cursor-pointer">
                  <td className="px-4 py-3 text-white">{l.name}</td>
                  <td className="px-4 py-3">
                    {l.lead_type === 'emergency'
                      ? <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/40">Emergency</span>
                      : <span className="text-xs px-2 py-0.5 rounded-full bg-slate-600/40 text-slate-300 border border-slate-600">Standard</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-300 truncate max-w-[180px]">{l.emergency_category || l.services?.join(', ') || '—'}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full border ${STAGE_COLORS[l.status] || ''}`}>{STAGE_LABELS[l.status] || l.status}</span></td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{l.source}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs uppercase">{l.language || 'en'}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{new Date(l.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
